import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { z } from 'zod';

import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Select } from '@sage/ui-components';
import { createFileRoute } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import { useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { selectCareRecipient, setCareRecipientDetails } from 'src/store/slices/onboardingSlice';
import { useAppDispatch } from 'src/store/store';

export const Route = createFileRoute('/(app)/onboarding/confirm-assisting')({
    component: ConfirmAssistingComponent,
});

const _careRecipientSchema = z.object({
    firstName: z.string().min(1, 'First name is required').trim(),
    lastName: z.string().min(1, 'Last name is required').trim(),
    pronouns: z.enum(['', 'HE_HIM_HIS', 'SHE_HER_HERS', 'THEY_THEM_THEIR'], {
        errorMap: () => ({ message: 'Please select pronouns' }),
    }),
    preferredName: z.string().trim().optional(),
});

type CareRecipientFormData = z.infer<typeof _careRecipientSchema>;

function ConfirmAssistingComponent() {
    const dispatch = useAppDispatch();

    const careRecipient = useSelector(selectCareRecipient);
    const { currentTeam } = useTeamMemberData();
    const { teamConfigurations } = currentTeam || {};

    const { progress, nextStep, previousStep, canGoBack } = useOnboardingWorkflow();

    const { handleSubmit, control, formState } = useForm<CareRecipientFormData>({
        mode: 'onChange',
        defaultValues: {
            firstName: careRecipient?.firstName ?? '',
            lastName: careRecipient?.lastName ?? '',
            pronouns: careRecipient?.pronouns ?? '',
            preferredName: careRecipient?.preferredName ?? '',
        },
    });

    const onSubmit = (data: CareRecipientFormData) => {
        dispatch(
            setCareRecipientDetails({
                firstName: data.firstName,
                lastName: data.lastName,
                pronouns: data.pronouns ? data.pronouns : undefined,
                preferredName: data.preferredName,
            })
        );

        nextStep();
    };

    return (
        <Box>
            <PageHeader
                hideBackButton={!canGoBack}
                onBack={canGoBack ? previousStep : undefined}
                hideActionButton
            >
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>
            <Stack sx={{ gap: 4, mb: 2.5 }}>
                <Stack sx={{ gap: 2 }}>
                    <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                        Confirm the care recipient&apos;s information
                    </Typography>
                </Stack>

                {teamConfigurations && (
                    <Stack
                        direction="row"
                        sx={{
                            p: 2.5,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: 3,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <Typography variant="body2">
                            {teamConfigurations.policyProviderName}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bolder' }}>
                            {teamConfigurations.policyId}
                        </Typography>
                    </Stack>
                )}

                <Controller
                    name="firstName"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="First Name"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                    rules={{ required: 'First name is required' }}
                />

                <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="Last Name"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                    rules={{ required: 'Last name is required' }}
                />

                <Controller
                    name="pronouns"
                    control={control}
                    rules={{ required: 'Please select pronouns' }}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <FormControl>
                            <InputLabel id="pronouns-label">Pronouns</InputLabel>
                            <Select
                                inputRef={ref}
                                labelId="pronouns-label"
                                label="Pronouns"
                                value={value ?? ''}
                                onChange={onChange}
                                error={!!error}
                            >
                                <MenuItem value="" disabled>
                                    Please specify pronouns
                                </MenuItem>
                                <MenuItem value="SHE_HER_HERS">She/Her</MenuItem>
                                <MenuItem value="HE_HIM_HIS">He/Him</MenuItem>
                                <MenuItem value="THEY_THEM_THEIR">They/Them</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                />

                <Controller
                    name="preferredName"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="Preferred Name (optional)"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                />
            </Stack>
            <PageFooter>
                <Button
                    disabled={!formState.isValid}
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSubmit(onSubmit)}
                >
                    Continue
                </Button>
            </PageFooter>
        </Box>
    );
}
